import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { AGENCE_USER } from "@/config/agenceNav";
import { useSession } from "@/session/SessionProvider";
import { dispatchDataError, describeSupabaseError } from "@/store/shared/dataErrors";
import { fetchProfilAgence, saveProfilAgence } from "@/store/platform/profilEspace";
import { removeLogo, resolveLogoDataUrl, uploadLogoDataUrl } from "@/store/shared/logos";
import type { AgencePreferences, AgenceProfil } from "./types";

interface ParametresContextValue {
  profil: AgenceProfil;
  preferences: AgencePreferences;
  /** Chargement initial du profil depuis la base. */
  chargement: boolean;
  /** L'utilisateur connecté peut-il modifier le profil de l'agence ? */
  peutEditer: boolean;
  updateProfil: (profil: AgenceProfil) => Promise<void>;
  updatePreferences: (preferences: AgencePreferences) => void;
}

const ParametresContext = createContext<ParametresContextValue | null>(null);

// Le nom de l'agence et les coordonnées restent vides tant qu'ils ne sont pas saisis
// (aucune donnée fictive). Le responsable est initialisé depuis l'utilisateur de l'espace.
const initialProfil: AgenceProfil = {
  nomAgence: "",
  responsable: AGENCE_USER.name,
  email: "",
  telephone: "",
  logo: "",
  logoPath: "",
  adresse: "",
  siteWeb: "",
  ifu: "",
  rccm: "",
};

const initialPreferences: AgencePreferences = {
  notifLoyer: true,
  notifPaiement: true,
  notifImpaye: true,
  notifContrat: true,
  notifEmploye: true,
  notifEmail: false,
  langue: "fr",
  devise: "XOF",
  formatDate: "jj/mm/aaaa",
};

/**
 * Profil de l'agence et préférences.
 * Le profil est lu et enregistré dans Supabase (`agences` + `comptes`) : seules
 * les informations AUTORISÉES (nom de l'agence, téléphone, responsable) sont
 * écrites. Seul le DG d'agence peut modifier le profil (contrôlé aussi côté
 * serveur par la RLS) ; un employé consulte en lecture seule.
 */
export function ParametresProvider({ children }: { children: ReactNode }) {
  const { session, refresh } = useSession();
  const compteId = session?.id ?? "";
  const peutEditer = session?.role === "dg_agence";
  const [profil, setProfil] = useState<AgenceProfil>(initialProfil);
  const [preferences, setPreferences] = useState<AgencePreferences>(initialPreferences);
  const [chargement, setChargement] = useState(false);
  // Périmètre Storage du compte (agence) : sert de dossier racine du logo.
  const [scopeId, setScopeId] = useState("");

  useEffect(() => {
    if (!compteId) return;
    let active = true;
    setChargement(true);
    (async () => {
      try {
        const data = await fetchProfilAgence(compteId);
        if (!active) return;
        setScopeId(data.agenceId);
        setProfil({
          nomAgence: data.nomAgence,
          responsable: data.responsable,
          email: data.email,
          telephone: data.telephone,
          adresse: data.adresse,
          siteWeb: data.siteWeb,
          ifu: data.ifu,
          rccm: data.rccm,
          logo: "",
          logoPath: data.logoPath,
        });
        if (data.logoPath) {
          const resolved = await resolveLogoDataUrl(data.logoPath);
          if (active && resolved) setProfil((prev) => ({ ...prev, logo: resolved }));
        }
      } catch (caught) {
        dispatchDataError({
          scope: "Profil",
          message: "Impossible de charger le profil de l'agence.",
          technical: describeSupabaseError(caught),
        });
      } finally {
        if (active) setChargement(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [compteId]);

  const updateProfil = useCallback(
    async (next: AgenceProfil) => {
      if (!peutEditer) return;
      if (!compteId) {
        setProfil(next);
        return;
      }
      // Logo : téléversement (nouveau), conservation ou suppression.
      let logoPath: string | null = next.logoPath || null;
      try {
        if (!next.logo) {
          logoPath = null;
          if (profil.logoPath) await removeLogo(profil.logoPath);
        } else if (scopeId && next.logo.startsWith("data:") && next.logo !== profil.logo) {
          logoPath = await uploadLogoDataUrl(scopeId, next.logo, profil.logoPath || undefined);
        }
      } catch (caught) {
        dispatchDataError({
          scope: "Profil",
          message: "Le téléversement du logo a échoué. Réessayez.",
          technical: describeSupabaseError(caught),
        });
        throw caught;
      }
      await saveProfilAgence(compteId, {
        nomAgence: next.nomAgence,
        telephone: next.telephone,
        responsable: next.responsable,
        adresse: next.adresse,
        siteWeb: next.siteWeb,
        ifu: next.ifu,
        rccm: next.rccm,
        logoPath,
      });
      setProfil({ ...next, email: profil.email, logoPath: logoPath ?? "" });
      await refresh();
    },
    [compteId, scopeId, profil, peutEditer, refresh]
  );

  const updatePreferences = useCallback((next: AgencePreferences) => setPreferences(next), []);

  const value = useMemo(
    () => ({ profil, preferences, chargement, peutEditer, updateProfil, updatePreferences }),
    [profil, preferences, chargement, peutEditer, updateProfil, updatePreferences]
  );

  return <ParametresContext.Provider value={value}>{children}</ParametresContext.Provider>;
}

export function useParametres(): ParametresContextValue {
  const context = useContext(ParametresContext);
  if (!context) {
    throw new Error("useParametres doit être utilisé à l'intérieur de ParametresProvider");
  }
  return context;
}